import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronDown, ChevronUp, Clock3, Minus, Pause, Play, Plus, RotateCcw } from "lucide-react";
import { formatTimer, getExerciseCounts, getSmartHoldSeconds } from "@/lib/exerciseTimer";

const PRESETS = [10, 20, 30, 45, 60];

export default function ExerciseTimer({ exercise }) {
  const holdSeconds = getSmartHoldSeconds(exercise);
  const { reps, sets } = getExerciseCounts(exercise);
  const exerciseKey = exercise?.id || exercise?.exercise_id || exercise?.exercise?.id || "exercise";
  const initialSeconds = holdSeconds || 30;
  const [selectedSeconds, setSelectedSeconds] = useState(initialSeconds);
  const [remaining, setRemaining] = useState(initialSeconds);
  const [customSeconds, setCustomSeconds] = useState("");
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [currentRep, setCurrentRep] = useState(1);
  const [currentSet, setCurrentSet] = useState(1);
  const audioContextRef = useRef(null);

  useEffect(() => {
    const nextSeconds = holdSeconds || 30;
    setSelectedSeconds(nextSeconds);
    setRemaining(nextSeconds);
    setRunning(false);
    setDone(false);
    setCurrentRep(1);
    setCurrentSet(1);
  }, [exerciseKey, holdSeconds, reps, sets]);

  const prepareAudio = () => {
    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (!AudioContext) return;
      if (!audioContextRef.current) audioContextRef.current = new AudioContext();
      if (audioContextRef.current.state === "suspended") audioContextRef.current.resume();
    } catch {}
  };

  const notifyFinished = useCallback(() => {
    try {
      const context = audioContextRef.current;
      if (context && context.state === "running") {
        const oscillator = context.createOscillator();
        const gain = context.createGain();
        oscillator.frequency.value = 660;
        gain.gain.setValueAtTime(0.08, context.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.001, context.currentTime + 0.25);
        oscillator.connect(gain);
        gain.connect(context.destination);
        oscillator.start();
        oscillator.stop(context.currentTime + 0.25);
      }
      if (navigator.vibrate) navigator.vibrate([120, 60, 120]);
    } catch {}
  }, []);

  useEffect(() => {
    if (!running) return undefined;
    const interval = window.setInterval(() => {
      setRemaining((current) => {
        if (current <= 1) {
          window.clearInterval(interval);
          setRunning(false);
          setDone(true);
          notifyFinished();
          return 0;
        }
        return current - 1;
      });
    }, 1000);
    return () => window.clearInterval(interval);
  }, [running, notifyFinished]);

  const chooseDuration = (seconds, shouldStart = false) => {
    setSelectedSeconds(seconds);
    setRemaining(seconds);
    setDone(false);
    setRunning(shouldStart);
    if (shouldStart) prepareAudio();
  };

  const start = () => {
    prepareAudio();
    if (remaining <= 0) setRemaining(selectedSeconds);
    setDone(false);
    setRunning(true);
  };

  const reset = () => {
    setRunning(false);
    setDone(false);
    setRemaining(selectedSeconds);
  };

  const applyCustomSeconds = () => {
    const parsedSeconds = Number.parseInt(customSeconds, 10);
    if (!Number.isFinite(parsedSeconds) || parsedSeconds <= 0) return;
    const seconds = Math.min(3600, parsedSeconds);
    chooseDuration(seconds);
    setCustomSeconds(String(seconds));
  };

  return (
    <div className="mt-3 rounded-xl border border-slate-100 bg-slate-50 p-2.5" data-testid="exercise-timer">
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => setExpanded((value) => !value)}
          className="inline-flex min-h-9 items-center gap-2 rounded-full bg-white px-3 text-xs font-semibold text-slate-700 shadow-sm"
          aria-expanded={expanded}
        >
          <Clock3 size={15} className="text-teal-600" />
          <span className="tabular-nums" aria-live="polite">{formatTimer(remaining)}</span>
          {done ? <span className="text-teal-700">Done</span> : null}
          {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>
        {holdSeconds ? (
          <button
            type="button"
            onClick={() => {
              setExpanded(true);
              chooseDuration(holdSeconds, true);
            }}
            className="min-h-9 rounded-full bg-teal-600 px-3 text-xs font-semibold text-white hover:bg-teal-700"
          >
            Start {holdSeconds} sec hold
          </button>
        ) : null}
      </div>

      {expanded ? (
        <div className="mt-3 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={running ? () => setRunning(false) : start}
              className="inline-flex min-h-9 items-center gap-1.5 rounded-full bg-slate-900 px-3 text-xs font-semibold text-white hover:bg-slate-800"
            >
              {running ? <Pause size={14} /> : <Play size={14} />}
              {running ? "Pause" : "Start"}
            </button>
            <button
              type="button"
              onClick={reset}
              className="inline-flex min-h-9 items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700"
            >
              <RotateCcw size={14} /> Reset
            </button>
            {done ? <span className="text-sm font-bold text-teal-700">Done</span> : null}
          </div>

          <div className="flex flex-wrap gap-1.5" aria-label="Timer presets">
            {PRESETS.map((seconds) => (
              <button
                key={seconds}
                type="button"
                onClick={() => chooseDuration(seconds)}
                className={`min-h-8 rounded-full px-2.5 text-xs font-semibold ${selectedSeconds === seconds ? "bg-teal-100 text-teal-800" : "bg-white text-slate-600"}`}
              >
                {seconds}s
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <label className="text-xs font-semibold text-slate-600" htmlFor={`custom-timer-${exerciseKey}`}>Custom</label>
            <input
              id={`custom-timer-${exerciseKey}`}
              type="number"
              min="1"
              max="3600"
              inputMode="numeric"
              value={customSeconds}
              onChange={(event) => setCustomSeconds(event.target.value)}
              onKeyDown={(event) => event.key === "Enter" && applyCustomSeconds()}
              placeholder="seconds"
              className="h-9 w-24 rounded-lg border border-slate-200 bg-white px-2 text-sm"
            />
            <button type="button" onClick={applyCustomSeconds} className="min-h-9 rounded-lg bg-white px-3 text-xs font-semibold text-slate-700 shadow-sm">Set</button>
          </div>

          {reps || sets ? (
            <div className="flex flex-wrap gap-2 border-t border-slate-200 pt-2">
              {reps ? (
                <Counter
                  label={holdSeconds ? "Hold" : "Rep"}
                  current={currentRep}
                  total={reps}
                  onChange={setCurrentRep}
                />
              ) : null}
              {sets ? <Counter label="Set" current={currentSet} total={sets} onChange={setCurrentSet} /> : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function Counter({ label, current, total, onChange }) {
  return (
    <div className="inline-flex items-center rounded-full bg-white p-1 text-xs font-semibold text-slate-700 shadow-sm">
      <button type="button" onClick={() => onChange(Math.max(1, current - 1))} className="grid h-7 w-7 place-items-center rounded-full text-slate-500" aria-label={`Previous ${label.toLowerCase()}`}>
        <Minus size={13} />
      </button>
      <span className="min-w-[82px] text-center">{label} {current} of {total}</span>
      <button type="button" onClick={() => onChange(Math.min(total, current + 1))} className="grid h-7 w-7 place-items-center rounded-full text-teal-700" aria-label={`Next ${label.toLowerCase()}`}>
        <Plus size={13} />
      </button>
    </div>
  );
}
